import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  APIActivateAccountResponseError,
  APIMemberData,
} from "@companion-core/shared/app/Interfaces/API";
import { IdentityCriteria } from "@companion-core/shared/app/Interfaces/member";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { useTrackingStore } from "@companion-core/shared/app/Store/trackingStore";
import { useVerifyMember } from "@companion-core/shared/app/Hooks/member";
import { useActivateAccount } from "@companion-core/shared/app/Queries";

export const useActivation = (vtoken: string | null, platform: string) => {
  const { t } = useTranslation();
  const { verifiedMemberData, isVerifyLoading } = useVerifyMember(vtoken ?? "");
  const activationModule = useMemo(() => getModule("register").modules.activation, []);
  const identityCriteriaList: IdentityCriteria[] = activationModule?.identityCriteria;
  const [activationContent, setActivationContent] = useState<string>("waiting-for-verification");
  const [activationErrorCode, setActivationErrorCode] = useState<number | null>(null);
  const [memberData, setMemberData] = useState<APIMemberData | null>(null);
  const { setCurrentPage } = useTrackingStore((state) => ({
    setCurrentPage: state.setCurrentPage,
  }));

  const { mutateAsync: activateAccount } = useActivateAccount();

  useEffect(() => {
    setCurrentPage();
  }, []);

  useEffect(() => {
    if (!isVerifyLoading) {
      if (verifiedMemberData) {
        setMemberData(verifiedMemberData as APIMemberData);
        setActivationContent("verified");
      } else {
        setMemberData(null);
        setActivationContent("verify-expired");
      }
    }
  }, [verifiedMemberData, isVerifyLoading]);

  const onSubmitIdentity = async (identityFormData?: Record<string, unknown>) => {
    if (identityFormData) {
      const newMemberData: APIMemberData = { ...memberData } as APIMemberData;
      Object.entries(identityFormData).forEach(([key, value]) => (newMemberData[key] = value));
      setMemberData(newMemberData);
    }
    setActivationContent("activation");
  };

  const onSubmitActivation = async (activationFormData?: Record<string, unknown>) => {
    if (memberData && activationFormData) {
      setActivationContent("waiting-for-verification");
      try {
        await activateAccount({
          memberData,
          activationFormData,
          identityCriteriaList,
          platform: platform,
        });
        setActivationContent("successful-activation");
      } catch (error: unknown) {
        setActivationErrorCode(
          (error as APIActivateAccountResponseError)?.response?.data?.code ?? null,
        );
        setActivationContent("activation-failed");
      }
    }
  };

  return {
    t,
    activationContent,
    activationErrorCode,
    onSubmitIdentity,
    onSubmitActivation,
    setActivationContent,
    memberData,
  };
};
